import { downloadProjectStatementInvoicesOrTimesheets, getStatementInvoicesOrTimesheets, pickUnpickAllProjectStatement } from 'lib/api';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import '@ag-grid-community/core/dist/styles/ag-grid.css';
import '@ag-grid-community/core/dist/styles/ag-theme-alpine.css';
import AG_GRID_LOCALE_IT from '@components/ag-grid/locale-it';
import { formatToDecimals } from '@lib/helpers/formatting';
import { InputGroup, FormControl, Tooltip, Button, OverlayTrigger, Row, Col, ButtonToolbar } from 'react-bootstrap';
import { Lens } from '@icons';
import { translate } from 'components/translation/translation';
import { IcoCheck, IcoLens } from 'icone';
import { columnCentered } from 'lib/helpers/agGridHelpers';
import DataHeader from 'components/DataHeader';
import DownloadExcelButton from 'components/form/DownloadExcelButton';
import CustomPagination from './CustomPagination';
import { debounce } from '@mui/material';

const ExpenseReportExternalCosts = ({
	projectId,
	detail,
	callback,
	pickLoading,
	setPickLoading,
	isValidated = false,
	amounts,
	selectedQuotas,
	setSelectedQuotaId,
	setShowModal,
	isShowModal,
}) => {
	const [invoices, setInvoices] = useState(null);
	const [count, setCount] = useState(0);
	const DEFAULT_PER_PAGE = 100;
	const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
	const [defaultPerPage, setDefaultPerPage] = useState(DEFAULT_PER_PAGE);
	const [loading, setLoading] = useState(true);
	const [selectedIds, setSelectedIds] = useState([]);
	const [currentPage, setCurrentPage] = useState(0);

	const gridRef = useRef();
	const localeText = AG_GRID_LOCALE_IT;

	const [filterInputValue, setFilterInputValue] = useState('');
	const [searchTerm, setSearchTerm] = useState('');

	const isFirstRenderRef = useRef(true);

	const handleData = (params) => {
		params.api.forEachNode((node) => {
			node.setSelected(node.data.checked === 1, false, true);
		});
	};

	const isSearchRef = useRef(false);

	useEffect(() => {
		if (isShowModal || isFirstRenderRef.current) {
			isFirstRenderRef.current = false;
			return;
		}

		setLoading(true);

		getStatementInvoicesOrTimesheets(
			projectId,
			detail.id,
			'invoices',
			'invoicesStatements',
			searchTerm === '' ? `?page=${currentPage + 1}&perPage=${perPage}` : `?page=${currentPage + 1}&perPage=${perPage}&term=${searchTerm}`,
		)
			.then((retrievedElements) => {
				//Confronta lo stato attuale con il nuovo stato prima di continuare
				if (retrievedElements.success && JSON.stringify(retrievedElements.data) !== JSON.stringify(invoices) && !isSearchRef.current) {
					isSearchRef.current = true;
					handleData(gridRef.current);
					setInvoices(retrievedElements.data);
					const { data, success, ...restResp } = retrievedElements;

					setCount(restResp.meta.total);
					setPerPage(restResp.meta.perPage);
					setDefaultPerPage(restResp.meta.perPage);
				} else {
					isSearchRef.current = false;
				}
			})
			.catch((error) => {
				console.error(error);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [isShowModal, projectId, detail.id, invoices, currentPage, perPage, searchTerm]);

	const StatusInfoCellRenderer = useCallback(
		(params) => {
			const isSelected = (params.selectedIds && params.selectedIds.includes(params.data.id)) || undefined;
			params.data.checked = isSelected ? 1 : 0;

			const green = '#689689';
			const yellow = '#D4BE31';
			let stoplightColor = '#ffffff';
			let message;

			if (params.value !== undefined) {
				if (params.queryParam === 'status') {
					if (params.value) {
						//check giallo, da validare
						stoplightColor = yellow;
						message = 'Da validare';
					} else {
						//check verde, rendicontabile
						stoplightColor = green;
						message = 'Rendicontabile';
					}
				} else if (params.queryParam === 'info' && params.data.checked === 1 && params.queryParam === 'info' && params.value !== 0) {
					message = 'Info';
				}

				const statusInfoTooltip = (props) => <Tooltip {...props}>{message}</Tooltip>;

				const settledComponent = () => {
					return (
						params.value !== 0 && (
							<Button
								variant="link"
								onClick={() => {
									setSelectedQuotaId(params.data.id);
									setShowModal(true);
								}}
							>
								<IcoLens className="edit" />
							</Button>
						)
					);
				};

				const checkIsAnalysis = () => {
					let bool = true;

					//Se è in analysisInvoices e non è stato selezionato
					if (params.queryParam1 === 'analysisInvoices' && !selectedQuotas.find((item) => item.id === params.data.id)) {
						bool = false;
					}

					return bool;
				};

				return (
					message && (
						<Row className="align-items-center justify-content-center h-100">
							<Col className="col-auto text-center">
								{params.queryParam === 'status' && (
									<OverlayTrigger placement="top" overlay={statusInfoTooltip}>
										<IcoCheck style={{ fill: stoplightColor }} />
									</OverlayTrigger>
								)}
								{params.queryParam === 'info' && params.data.checked === 1 && checkIsAnalysis() && settledComponent()}
							</Col>
						</Row>
					)
				);
			}
		},
		[selectedQuotas, setShowModal, setSelectedQuotaId],
	);

	const defaultColDef = {
		resizable: false,
		flex: 1,
		suppressMovable: true,
		sortable: false,
		filterParams: {
			suppressAndOrCondition: true,
			filterOptions: ['contains'],
		},
		cellStyle: {
			padding: '3px 1rem',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
		},
	};

	const columnDefs = useMemo(
		() => [
			{
				headerName: '',
				hide: isValidated ? true : false,
				maxWidth: 50,
				resizable: false,
				headerCheckboxSelection: true,
				checkboxSelection: isValidated ? false : true,
				cellStyle: () => {
					//per disabilitare il click sulla riga in loading
					return loading || pickLoading ? { pointerEvents: 'none', opacity: '0.4' } : null;
				},
			},
			{
				headerName: 'Nr. Fattura',
				field: 'number',
				filter: 'agTextColumnFilter',
			},
			{
				headerName: 'ID Fattura',
				field: 'rpCode',
				filter: 'agTextColumnFilter',
			},
			{
				headerName: 'Fornitore',
				field: 'supplierName',
				filter: 'agTextColumnFilter',
			},
			{
				headerName: 'Voce di spesa',
				// field: 'expenseItemName',
				field: 'noticeExpenseItemName',
				filter: 'agTextColumnFilter',
			},
			{
				headerName: 'Importo rendicontabile €',
				field: 'amount',
				filter: 'agTextColumnFilter',
				type: 'rightAligned',
				resizable: true,
				valueGetter: (params) => {
					return params.data.amount ? formatToDecimals(params.data.amount) : 0;
				},
			},
			{
				headerName: 'Status',
				field: 'toSettle',
				cellRenderer: StatusInfoCellRenderer,
				cellRendererParams: {
					queryParam: 'status',
					selectedIds,
				},
				minWidth: 90,
				maxWidth: 90,
				...columnCentered(),
			},
			{
				headerName: 'Info',
				field: 'toSettle',
				cellRenderer: StatusInfoCellRenderer,
				cellRendererParams: {
					queryParam: 'info',
					queryParam1: 'invoices',
					selectedIds,
				},
				minWidth: 90,
				maxWidth: 90,
				...columnCentered(),
			},
		],
		[isValidated, StatusInfoCellRenderer, loading, pickLoading, selectedIds],
	);

	const handlePickAll = async (action, rows) => {
		if (rows.length === 0) return;
		setPickLoading(true);
		await pickUnpickAllProjectStatement(projectId, detail.id, 'invoices', action, {
			quotaIds: rows ? rows : invoices.map((t) => t.id),
		}).finally(() => {
			setPickLoading(false);
		});
	};

	useEffect(() => {
		getStatementInvoicesOrTimesheets(projectId, detail.id, 'invoices', 'invoicesStatements')
			.then((response) => {
				if (response.data && response.success) {
					const { data, success, ...restResp } = response;
					setCount(restResp.meta.total);
					setInvoices(response.data);
					setSelectedIds(response.data.filter((t) => t.checked === 1).map((t) => t.id));
				}
			})
			.catch((error) => {
				console.error(error);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [projectId, detail.id]);

	const debouncedSetSearchTermRef = useRef(
		debounce((value) => {
			setCurrentPage(0);
			setPerPage(defaultPerPage);
			if (gridRef.current) {
				gridRef.current.api.paginationGoToPage(0);
				gridRef.current.api.paginationSetPageSize(defaultPerPage);
			}
			setSearchTerm(value); // Solo l'aggiornamento finale dopo debounce
		}, 500),
	);
	const onFilterChange = (e) => {
		const value = e.target.value;
		setFilterInputValue(value);
	};

	useEffect(() => {
		debouncedSetSearchTermRef.current(filterInputValue);
	}, [filterInputValue]);

	return (
		<>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<DataHeader
					customStyle={{
						margin: 0,
						paddingTop: '5px',
						paddingBottom: 0,
						marginLeft: '15px',
					}}
					data={[
						{
							name: (
								<span className="text-nowrap">{translate('common.externalcosts.totalEligibles', 'Importo Rendicontato Eleggibile')}</span>
							),
							value: Object.keys(amounts).length > 0 && formatToDecimals(amounts.totalEligibles ?? 0) + ' €',
						},
						{
							name: (
								<span className="text-nowrap">
									{translate('common.externalcosts.totalEligibles', 'Importo Rendicontato Eleggibile')} -{' '}
									{translate('common.externalcosts.totalEligiblesToValidate', 'Elementi da validare')}
								</span>
							),
							value: Object.keys(amounts).length > 0 && formatToDecimals(amounts.totalToSettle ?? 0) + ' €',
						},
					]}
				/>
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
					}}
				>
					<ButtonToolbar className="justify-content-end">
						<DownloadExcelButton
							apiDownload={downloadProjectStatementInvoicesOrTimesheets}
							apiProps={[projectId, detail.id, 'invoices']}
							label={translate('common.downloadreport', 'Scarica report')}
						/>

						<InputGroup className="search-table align-items-center mt-md-0 ms-1" style={{ width: '300px' }}>
							<FormControl
								type="text"
								className="filter-text-box"
								placeholder={translate('common.search', 'Cerca') + '...'}
								value={filterInputValue}
								onChange={onFilterChange}
							/>
							<div style={{ width: '40px', textAlign: 'center' }}>
								<Lens className="lens" />
							</div>
						</InputGroup>
					</ButtonToolbar>
				</div>
			</div>
			<div className="ag-theme-alpine list-table">
				<Row>
					<Col className="col-12 col-xxl-9" id="grid-wrapper">
						<AgGridReact
							ref={gridRef}
							columnDefs={columnDefs}
							rowData={invoices || []}
							//   rowData={test(invoices) || []}
							loading={loading || pickLoading}
							defaultColDef={defaultColDef}
							domLayout="autoHeight"
							tooltipShowDelay={0}
							tooltipHideDelay={2000}
							tooltipMouseTrack={true}
							colResizeDefault={'shift'}
							headerHeight={50}
							rowHeight={50}
							ariaDescription="ag-grid-table"
							suppressCellFocus={true}
							rowSelection={'multiple'}
							onFirstDataRendered={handleData}
							onGridReady={handleData}
							suppressPaginationPanel={true}
							onSelectionChanged={async (e) => {
								const selectedRows = [];
								const unselectedRows = [];

								// Check if user has filtered the table
								if (e.api.getDisplayedRowCount() !== invoices.length) {
									// If the table is filtered, we need to loop through all the nodes
									e.api.forEachNodeAfterFilter((node) => {
										// If the node is selected, push it to the selectedRows array
										if (node.isSelected()) {
											selectedRows.push(node.data);
										} else {
											// If the node is not selected, push it to the unselectedRows array
											unselectedRows.push(node.data);
										}
									});
								} else {
									// If user has not filtered the table, we can use the selectedRows array
									selectedRows.push(...e.api.getSelectedRows());

									// We can also use the unselectedRows array
									unselectedRows.push(...invoices.filter((t) => !selectedRows.some((s) => s.id === t.id)));
								}
								// Update the state with the selected IDs
								setSelectedIds((prevSelectedIds) => {
									// Remove IDs that are in unselectedRows
									const remainingIds = prevSelectedIds.filter((id) => !unselectedRows.some((row) => row.id === id));

									// Add IDs from selectedRows that aren't already in the state
									const newIds = selectedRows.map((row) => row.id).filter((id) => !remainingIds.includes(id));

									return [...remainingIds, ...newIds];
								});

								const picksPromises = [
									handlePickAll(
										'unpickall',
										unselectedRows.map((s) => s.id),
									),
									handlePickAll(
										'pickall',
										selectedRows.map((s) => s.id),
									),
								];

								await Promise.all(picksPromises);
								callback && callback();
							}}
							onFilterChanged={(params) => {
								// When the filter changes, we need to update the selected rows
								params.api.forEachNode((node) => {
									node.setSelected(selectedIds.includes(node.data.id), false, true);
								});
							}}
							suppressRowClickSelection={true}
							rowMultiSelectWithClick={true}
							localeText={localeText}
							pagination={true}
							paginationPageSize={100}
							onPaginationChanged={(e, newPage) => {
								if (e.newData === undefined) return;
								handleData(e);
							}}
						/>
					</Col>
					<CustomPagination
						count={count}
						loading={loading}
						currentPage={currentPage}
						setCurrentPage={setCurrentPage}
						perPage={perPage}
						setPerPage={setPerPage}
						gridRef={gridRef}
					/>
				</Row>
			</div>
		</>
	);
};

export default ExpenseReportExternalCosts;
